
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import './RichTextEditor.scss';
import { ApiPost } from '../../../helpers/API/ApiData';

// Predefined templates
const templates = {
  blank: '',
  aboutUs: `
    <div style="max-width: 1200px; margin: 0 auto; padding: 2rem;">
      <h1 style="font-size: 24px; font-weight: bold; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid #e5e7eb;">About Us</h1>
      
      <div style="margin-bottom: 3rem;">
        <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb;">Our Mission & Vision</h2>
        <div style="display: flex; gap: 1.5rem;">
          <div style="flex: 2;">
            <p style="color: #4B5563; line-height: 1.6;">Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.</p>
          </div>
          <div style="flex: 1;">
            <img src="[Insert Mission Image URL]" alt="Mission" style="width: 100%; height: 200px; object-fit: cover; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          </div>
        </div>
      </div>

      <div style="margin-bottom: 3rem;">
        <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb;">Department of Social Justice</h2>
        <div style="display: flex; gap: 1.5rem;">
          <div style="flex: 2;">
            <p style="color: #4B5563; line-height: 1.6;">Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.</p>
          </div>
          <div style="flex: 1;">
            <img src="[Insert Social Justice Image URL]" alt="Social Justice" style="width: 100%; height: 200px; object-fit: cover; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          </div>
        </div>
      </div>

      <div style="margin-bottom: 3rem;">
        <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb;">Department of Empowerment</h2>
        <div style="display: flex; gap: 1.5rem;">
          <div style="flex: 2;">
            <p style="color: #4B5563; line-height: 1.6;">Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.</p>
          </div>
          <div style="flex: 1;">
            <img src="[Insert Empowerment Image URL]" alt="Empowerment" style="width: 100%; height: 200px; object-fit: cover; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          </div>
        </div>
      </div>

      <div style="margin-bottom: 3rem;">
        <h2 style="font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb;">Department of Tribal Affairs</h2>
        <div style="display: flex; gap: 1.5rem;">
          <div style="flex: 2;">
            <p style="color: #4B5563; line-height: 1.6;">Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book.</p>
          </div>
          <div style="flex: 1;">
            <img src="[Insert Tribal Affairs Image URL]" alt="Tribal Affairs" style="width: 100%; height: 200px; object-fit: cover; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
          </div>
        </div>
      </div>
    </div>
  `,
};

// Editor options
const editorOptions = {
  height: 'auto',
  minHeight: '500px',
  resizingBar: false,
  buttonList: [
    ['undo', 'redo'],
    ['font', 'fontSize', 'formatBlock'],
    ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
    ['removeFormat'],
    '/',
    ['fontColor', 'hiliteColor'],
    ['align', 'list', 'lineHeight'],
    ['outdent', 'indent'],
    ['table', 'link', 'image'],
    ['showBlocks', 'codeView'],
    ['preview'],
  ],
  formats: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  font: [
    'Arial',
    'Comic Sans MS',
    'Courier New',
    'Impact',
    'Georgia',
    'Tahoma',
    'Trebuchet MS',
    'Verdana',
  ],
  imageAccept: '.jpg, .jpeg, .png, .gif',
  imageResizing: true,
  imageFileInput: true,
  imageUploadSizeLimit: 5 * 1024 * 1024, // 5MB
  imageMultipleFile: true,
  videoResizing: true,
  videoFileInput: false,
  videoUrlInput: true,
};

const mediaButtonList = [
  ['undo', 'redo'],
  ['formatBlock'],
  ['bold', 'underline', 'italic'],
  ['removeFormat'],
  ['align', 'list'],
  ['outdent', 'indent'],
  ['link'],
];

const RichTextEditor = ({
  content,
  setContent,
  compact = false,
  disabled = false,
  mediaMode = false,
  label,
}) => {
  const [editorContent, setEditorContent] = useState(content || '');
  const editorRef = useRef();

  const options = useMemo(() => {
    if (!mediaMode) return editorOptions;
    return {
      ...editorOptions,
      buttonList: mediaButtonList,
      formats: ['p', 'h2', 'h3', 'h4'],
      imageFileInput: false,
      imageUrlInput: false,
      minHeight: compact ? '112px' : '220px',
      videoFileInput: false,
      videoUrlInput: false,
    };
  }, [compact, mediaMode]);

  useEffect(() => {
    const nextContent = content || '';
    if (nextContent !== editorContent) {
      setEditorContent(nextContent);
    }
  }, [content, editorContent]);

  const getSunEditorInstance = (sunEditor) => {
    editorRef.current = sunEditor;
  };

  const handleEditorChange = (newContent) => {
    setEditorContent(newContent);
    setContent(newContent);
  };

  const handleTemplateChange = (e) => {
    const templateContent = templates[e.target.value];
    setEditorContent(templateContent);
    setContent(templateContent);
  };

  const handleImageUploadBefore = async (files, info, uploadHandler) => {
    try {
      const uploadedImages = [];
      // Process images one by one since API handles single file
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('platform', 'web'); // Required by API

        // Call the image upload API using ApiPost
        const res = await ApiPost('images/text-editor/images', formData);

        console.log("resssssssssssssaaa",res)
        if (res.data && res.data.success && res.data.data) {


          uploadedImages.push(...res.data.data);
        } else {
          throw new Error(res.data.message || 'Image upload failed');
        }
      }

      // Create media objects for BlogDialog
      const newMedia = uploadedImages.map((img) => ({
        type: 'image',
        url: img.url,
        file: files.find((f) => f.name === img.name), // Match file by name
        caption: '',
      }));

      // Add images to BlogDialog's formData.media
      // addMedia(newMedia);

      // Pass image URLs to SunEditor
      uploadHandler({
        result: uploadedImages,
      });

      return true;
    } catch (error) {
      console.error('Image upload error:', error);
      uploadHandler({ result: [] }); // Handle error gracefully
      return false;
    }
  };

  return (
    <div
      aria-label={label}
      className={`rich-text-editor-container${mediaMode ? ' rich-text-editor-container--media' : ''}${compact ? ' rich-text-editor-container--compact' : ''}`}
    >
      {/* Uncomment to enable template selection */}
      {/* <select
        onChange={handleTemplateChange}
        className="template-select"
        defaultValue="blank"
      >
        <option value="blank">Blank</option>
        <option value="aboutUs">About Us</option>
      </select> */}
      <SunEditor
        lang="en"
        getSunEditorInstance={getSunEditorInstance}
        setContents={editorContent}
        onChange={handleEditorChange}
        setOptions={options}
        disable={disabled}
        onImageUploadBefore={mediaMode ? undefined : handleImageUploadBefore}
        onImageUploadError={(errorMessage, result) => {
          console.error('Image upload error:', errorMessage, result);
        }}
        defaultValue={mediaMode ? '' : 'Start writing here...'}
      />
    </div>
  );
};

export default RichTextEditor;
